import { useQuery } from '@apollo/client';
import {
  GET_BUSINESS_FUNCTION_WORKAROUND_STATS,
  GET_BUSINESS_FUNCTION_WORKAROUNDS,
} from './queries';
import { useState } from 'react';
import { checkForWorkaroundsAffectedByITAppRemoval } from '../../../../utils/checkForWorkaroundsAffectedByITAppRemoval';
import {
  BusinessFunctionStats,
  BusinessFunctionWorkaround,
} from '../../../../shared/types/business-functions-workarounds';

type AffectedWorkaroundApplication = { name: string; workaroundUrl: string }[];

/**
 * Hook to get business function workarounds stats and update workarounds
 * @param locationId - The location ID
 * @returns An object containing the stats, loading, error, and functions to get and update workarounds
 */
export const useLocationBusinessFunctionWorkarounds = (
  locationUUID: string,
) => {
  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
  } = useQuery(GET_BUSINESS_FUNCTION_WORKAROUND_STATS, {
    variables: { locationUUID },
    skip: !locationUUID,
  });

  return {
    stats:
      statsData?.getBusinessFunctionWorkaroundStats as BusinessFunctionStats[],
    statsLoading,
    statsError,
  };
};

/**
 * Hook to get business function workarounds by business function id
 * @param businessFunctionIds - The business function ID
 * @param locationId - The location ID
 * @returns An object containing the workarounds, loading, and error
 */
export const useBusinessFunctionApplicationWorkarounds = (
  businessFunctionIds?: string[],
  locationId?: string,
) => {
  const variables = {
    data: {
      ...(locationId && { locationUuid: locationId }),
      uuids: businessFunctionIds,
    },
  };

  const { data, loading, error, refetch } = useQuery(
    GET_BUSINESS_FUNCTION_WORKAROUNDS,
    {
      variables,
    },
  );

  return {
    data: data?.getBusinessFunctionWorkarounds,
    loading,
    error,
    refetch,
  };
};

type ITApplicationToRemove = { uuid: string }[];

/**
 * Generate the URL for a workaround to redirect to the appropriate page
 * @param workaround - The workaround object
 * @param locationId - The location ID
 * @returns The URL for the workaround
 */
export const getWorkaroundUrl = (
  workaround: any,
  locationId?: string,
): string => {
  let baseUrl = '/cyber/enterprise-level';
  let specificPath = '';

  if (locationId) {
    baseUrl = `/cyber/location-level/${locationId}`;
  }

  // Check for Business Function
  if (workaround.businessFunctionId) {
    specificPath = `/business-functions-workarounds/${workaround.businessFunctionId}`;
    if (!locationId) {
      // Enterprise level has a different structure
      baseUrl = '/cyber/enterprise-level/application-workarounds';
      specificPath = `/business-functions/${workaround.businessFunctionId}/summary`;
    }
  }
  // Check for Qualitative Factor
  else if (workaround.qualitativeFactorId) {
    specificPath = `/qualitative-factors/workarounds/${workaround.qualitativeFactorId}`;
    if (!locationId) {
      // Enterprise level for QF
      baseUrl = '/cyber/enterprise-level';
      specificPath = `/qualitative-factors/workarounds/${workaround.qualitativeFactorId}`;
    }
  } else {
    console.warn('Unknown workaround type, cannot determine URL:', workaround);
    return '/cyber/unknown-workaround-type';
  }

  return `${baseUrl}${specificPath}`;
};

type RemoveITApplicationConfirmationProps = {
  workarounds: BusinessFunctionWorkaround[] | any[];
  locationId?: string;
};

/**
 * Hook to handle the confirmation flow for the RemoveITApplicationModal.
 * Shows a confirmation modal when removing IT applications that have workarounds.
 */
export const useRemoveITApplicationConfirmation = ({
  workarounds,
  locationId,
}: RemoveITApplicationConfirmationProps) => {
  const [isModalOpen, setModalOpen] = useState(false);

  const [affectedApplications, setAffectedApplications] =
    useState<AffectedWorkaroundApplication>([]);

  // Callback to execute when user confirms the removal
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);

  /**
   * Checks if removing IT applications would affect any workarounds.
   * If yes, shows the confirmation modal. If no, proceeds with the update immediately.
   */
  const handleBeforeUpdate = (
    appsBeingRemoved: ITApplicationToRemove,
    onProceed: () => void,
  ) => {
    // Check if removing IT applications would affect any existing workarounds
    const { hasUpdatedWorkarounds } = checkForWorkaroundsAffectedByITAppRemoval(
      workarounds,
      appsBeingRemoved,
    );

    if (hasUpdatedWorkarounds) {
      // Get the affected workarounds that have been updated
      const affectedWorkarounds = workarounds.filter((workaround) => {
        // Check if this workaround's IT application is being removed
        const isBeingRemoved = appsBeingRemoved.some(
          (item) => item.uuid === workaround.itApplicationId,
        );

        // Check if the workaround has been updated (has a non-null updatedAt)
        const hasBeenUpdated = workaround.workaroundUpdatedAt !== null;

        // Include in result if both conditions are true: IT app is being removed AND workaround was updated
        return isBeingRemoved && hasBeenUpdated;
      });

      // Transform the affected workarounds into the format needed for the modal
      const uniqueAffectedApps = new Map<
        string,
        { name: string; workaroundUrl: string }
      >();
      affectedWorkarounds.forEach((workaround) => {
        // Ensure workaround.itApplicationId is not null or undefined before using it as a map key
        if (
          workaround.itApplicationId &&
          !uniqueAffectedApps.has(workaround.itApplicationId)
        ) {
          uniqueAffectedApps.set(workaround.itApplicationId, {
            name:
              workaround.itApplicationName ||
              `IT Application ${workaround.itApplicationId}`,
            workaroundUrl: getWorkaroundUrl(workaround, locationId),
          });
        }
      });
      const transformedWorkarounds = Array.from(uniqueAffectedApps.values());

      setAffectedApplications(transformedWorkarounds);
      setOnConfirm(() => onProceed);
      setModalOpen(true);
    } else {
      onProceed();
    }
  };

  /**
   * Called when user clicks "Remove Applications" in the modal.
   * Executes the stored proceed callback to complete the removal.
   */
  const handleConfirm = () => {
    onConfirm?.(); // Execute the proceed callback if it exists
    setModalOpen(false);
    setOnConfirm(null);
  };

  /**
   * Called when user clicks "Cancel" in the modal.
   * Closes the modal without executing the proceed callback.
   */
  const handleCancel = () => {
    setModalOpen(false);
    setOnConfirm(null);
  };

  return {
    isModalOpen,
    handleBeforeUpdate,
    affectedApplications,
    handleConfirm,
    handleCancel,
  };
};
